"use client";

// Feedback thumbs + modal for assistant messages, driven by the injected
// `feedback` source prop (absent → no thumbs, today's degraded behavior).
// The block owns the modal, the submit timing (single retry when the
// assistant row hasn't persisted yet), and the comment length cap; the
// transport is the host's — `submit` on the source prop (tRPC in the web
// app, `magnus.feedback.*` in SDK embeds).
import { c as _c } from "react/compiler-runtime";
import type { SdkFeedbackMap } from "@bg-bgi/magnus-sdk";
import { Button } from "@workspace/ui/components/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@workspace/ui/components/dialog";
import { Switch } from "@workspace/ui/components/switch";
import { Textarea } from "@workspace/ui/components/textarea";
import { CheckIcon, ThumbsDownIcon, ThumbsUpIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/** Matches the server-side cap (the plan-015 zod schema). */
export const FEEDBACK_COMMENT_MAX_LENGTH = 2000;

/**
 * The assistant row is persisted when the turn ends (or is superseded),
 * which can briefly lag the live stream. A `NOT_FOUND` immediately after a
 * response usually means the row simply hasn't landed yet, so we retry once
 * after this delay before surfacing an error.
 */
const FEEDBACK_NOT_FOUND_RETRY_MS = 1200;
export interface FeedbackSubmitInput {
  comment?: string;
  includeFullConversation: boolean;
  messageId: string;
  vote: "up" | "down";
}

/**
 * The transcript feedback seam (`MagnusChatProps.feedback`). Absent → no
 * thumbs. `submit` is the host's transport; it must reject with an error
 * carrying a NOT_FOUND-ish code (`err.data.code`, `err.code`, or an
 * `HTTP 404` message) for the persistence-gap retry to engage.
 */
export interface ChatFeedbackSource {
  /**
   * Whether this viewer may vote. Feedback is owner-only server-side — it
   * captures the owner's conversation as review context — so a collaborator
   * on a shared chat gets a 403. Pass `false` for them and the thumbs are
   * hidden rather than shown-and-rejected. Defaults to `true`; hosts with no
   * sharing model can ignore it.
   */
  canSubmit?: boolean;
  /** Thumbs state for the active chat, keyed by message id. */
  map: SdkFeedbackMap;
  pending?: boolean;
  submit: (input: FeedbackSubmitInput) => Promise<void> | void;
}
export interface UseFeedbackSubmitResult {
  /** User-facing error from the last submit, or null. */
  error: string | null;
  isPending: boolean;
  /** Clear the current error. */
  reset: () => void;
  submit: (input: FeedbackSubmitInput) => void;
}

/**
 * Classify a feedback-submit failure for the retry/user-message logic.
 * tRPC errors carry `data.code` (or a top-level `code`); the versioned
 * REST surface throws plain Errors with the status in the message (no
 * error classes in v1). Exported (and pure) so the classification is
 * unit-testable without a React renderer.
 */
export function feedbackErrorCode(err: unknown): string | undefined {
  if (!err || typeof err !== "object") {
    return undefined;
  }
  const data = (err as {
    data?: {
      code?: unknown;
    };
  }).data;
  if (typeof data?.code === "string") {
    return data.code;
  }
  const code = (err as {
    code?: unknown;
  }).code;
  if (typeof code === "string") {
    return code;
  }
  if (err instanceof Error && /HTTP 404\b/.test(err.message)) {
    return "NOT_FOUND";
  }
  return undefined;
}

/**
 * Headless feedback submission for a chat message. Owns the persistence-gap
 * retry (UI-layer timing, not transport), pending/error state, and the
 * failure toast; presentational modals/menus compose on top.
 */
export function useFeedbackSubmit({
  submit: submitTransport,
  onSuccess
}: {
  submit: (input: FeedbackSubmitInput) => Promise<void> | void;
  onSuccess?: () => void;
}): UseFeedbackSubmitResult {
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  // `fire` is referenced by the retry path before its declaration, so we
  // reach it through a ref to sidestep the cycle.
  const fireRef = useRef<(input: FeedbackSubmitInput) => void>(() => {
    // Reassigned to `fire` below on every render.
  });
  const retriedRef = useRef(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastInputRef = useRef<FeedbackSubmitInput | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const submitTransportRef = useRef(submitTransport);
  submitTransportRef.current = submitTransport;
  const fire = useCallback((input: FeedbackSubmitInput) => {
    setIsPending(true);
    Promise.resolve(submitTransportRef.current({
      ...input,
      comment: input.comment?.trim().slice(0, FEEDBACK_COMMENT_MAX_LENGTH) || undefined
    })).then(() => {
      setIsPending(false);
      retriedRef.current = false;
      setError(null);
      onSuccessRef.current?.();
    }, (err: unknown) => {
      const code = feedbackErrorCode(err);

      // Persistence-timing gap: the assistant row lands a beat after the
      // stream. Retry the same submission once before giving up so the
      // common case self-heals without the user seeing an error.
      if (code === "NOT_FOUND" && !retriedRef.current) {
        retriedRef.current = true;
        const lastInput = lastInputRef.current;
        if (lastInput) {
          retryTimerRef.current = setTimeout(() => {
            setIsPending(true);
            fireRef.current(lastInput);
          }, FEEDBACK_NOT_FOUND_RETRY_MS);
          return;
        }
      }
      setIsPending(false);
      retriedRef.current = false;
      const isUserFacing = code && code !== "INTERNAL_SERVER_ERROR";
      const message = code === "NOT_FOUND" ? "We couldn't find this response to attach your feedback to. Please refresh and try again." : isUserFacing && err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setError(message);
      toast.error("Failed to submit feedback", {
        description: message
      });
    });
  }, []);
  fireRef.current = fire;
  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);
  useEffect(() => clearRetryTimer, [clearRetryTimer]);
  const reset = useCallback(() => {
    clearRetryTimer();
    retriedRef.current = false;
    setError(null);
  }, [clearRetryTimer]);
  const submit = useCallback((input_0: FeedbackSubmitInput) => {
    clearRetryTimer();
    retriedRef.current = false;
    lastInputRef.current = input_0;
    setError(null);
    fire(input_0);
  }, [clearRetryTimer, fire]);
  return {
    submit,
    isPending,
    error,
    reset
  };
}

/**
 * After the feedback row is persisted we hold the modal open briefly on a
 * "Submitted" confirmation before dismissing. The async submit path landed
 * the row so fast that the modal vanished instantly, which read as jarring
 * — this short beat lets the success state register before the modal
 * closes.
 */
const FEEDBACK_SUCCESS_DISPLAY_MS = 1500;
export type FeedbackModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId: string;
  vote: "up" | "down";
  /** The host transport (the `feedback` source's `submit`). */
  submit: (input: FeedbackSubmitInput) => Promise<void> | void;
  onSuccess: () => void;
};
export function FeedbackModal(t0) {
  const $ = _c(77);
  const {
    open,
    onOpenChange,
    messageId,
    vote,
    submit: submitTransport,
    onSuccess
  } = t0;
  const [comment, setComment] = useState("");
  const [includeFullConversation, setIncludeFullConversation] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const closeTimerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const clearCloseTimer = t1;
  let t2;
  if ($[1] !== onOpenChange || $[2] !== onSuccess) {
    t2 = () => {
      setIsSubmitted(true);
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null;
        setComment("");
        setIncludeFullConversation(true);
        setIsSubmitted(false);
        onOpenChange(false);
        onSuccess();
      }, FEEDBACK_SUCCESS_DISPLAY_MS);
    };
    $[1] = onOpenChange;
    $[2] = onSuccess;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== submitTransport || $[5] !== t2) {
    t3 = {
      submit: submitTransport,
      onSuccess: t2
    };
    $[4] = submitTransport;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    submit,
    isPending,
    error,
    reset
  } = useFeedbackSubmit(t3);
  let t4;
  let t5;
  if ($[7] !== open || $[8] !== reset) {
    t4 = () => {
      if (open) {
        clearCloseTimer();
        setComment("");
        setIncludeFullConversation(true);
        setIsSubmitted(false);
        reset();
      }
    };
    t5 = [open, reset, clearCloseTimer];
    $[7] = open;
    $[8] = reset;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => clearCloseTimer;
    t7 = [clearCloseTimer];
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  const isBusy = isPending || isSubmitted;
  let t8;
  if ($[13] !== comment || $[14] !== includeFullConversation || $[15] !== messageId || $[16] !== submit || $[17] !== vote) {
    t8 = () => {
      submit({
        messageId,
        vote,
        comment,
        includeFullConversation
      });
    };
    $[13] = comment;
    $[14] = includeFullConversation;
    $[15] = messageId;
    $[16] = submit;
    $[17] = vote;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleSubmit = t8;
  let t9;
  if ($[19] !== isBusy || $[20] !== onOpenChange || $[21] !== reset) {
    t9 = next => {
      if (!(next || isBusy)) {
        setComment("");
        setIncludeFullConversation(true);
        reset();
      }
      if (next || !isBusy) {
        onOpenChange(next);
      }
    };
    $[19] = isBusy;
    $[20] = onOpenChange;
    $[21] = reset;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleOpenChange = t9;
  let t10;
  if ($[23] !== vote) {
    t10 = vote === "up" ? <ThumbsUpIcon className="size-4 text-success" /> : <ThumbsDownIcon className="size-4 text-destructive" />;
    $[23] = vote;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = vote === "up" ? "What went well?" : "What went wrong?";
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <DialogHeader><DialogTitle className="flex items-center gap-2">{t10}{t11}</DialogTitle></DialogHeader>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = vote === "up" ? "Glad this was helpful! Let us know what worked so we can keep improving Magnus." : "Sorry about that. Your feedback helps us improve Magnus\u2019s features and knowledge.";
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <a className="underline underline-offset-2" href="mailto:magnus@brasfieldgorrie.com">magnus@brasfieldgorrie.com</a>;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t13) {
    t15 = <DialogDescription>{t13}{" "}This won't typically result in you being contacted. For immediate help, email{" "}{t14}.</DialogDescription>;
    $[29] = t13;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = !!error;
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = e => setComment(e.target.value);
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const t18 = vote === "up" ? "What did you find helpful?" : "What could be improved?";
  let t19;
  if ($[32] !== comment || $[33] !== isBusy || $[34] !== t16 || $[35] !== t18) {
    t19 = <Textarea aria-invalid={t16} className="resize-none my-5" disabled={isBusy} maxLength={FEEDBACK_COMMENT_MAX_LENGTH} onChange={t17} placeholder={t18} rows={3} value={comment} />;
    $[32] = comment;
    $[33] = isBusy;
    $[34] = t16;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== error) {
    t20 = error && <p className="text-sm text-destructive" role="alert">{error}</p>;
    $[37] = error;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== includeFullConversation || $[40] !== isBusy) {
    t21 = <Switch checked={includeFullConversation} className="mt-0.5" disabled={isBusy} onCheckedChange={setIncludeFullConversation} size="sm" />;
    $[39] = includeFullConversation;
    $[40] = isBusy;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <p className="text-sm font-medium leading-tight">Include full conversation</p>;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  const t23 = includeFullConversation ? "The entire conversation will be stored for review." : "Only this message and the one before it will be stored.";
  let t24;
  if ($[43] !== t23) {
    t24 = <div className="select-none">{t22}<p className="text-xs text-muted-foreground mt-0.5">{t23}</p></div>;
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t21 || $[46] !== t24) {
    t25 = <label className="flex items-start gap-3 cursor-pointer">{t21}{t24}</label>;
    $[45] = t21;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== t15 || $[49] !== t19 || $[50] !== t20 || $[51] !== t25) {
    t26 = <div className="space-y-3 -mt-2">{t15}{t19}{t20}{t25}</div>;
    $[48] = t15;
    $[49] = t19;
    $[50] = t20;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== handleOpenChange) {
    t27 = () => handleOpenChange(false);
    $[53] = handleOpenChange;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== isBusy || $[56] !== t27) {
    t28 = <Button disabled={isBusy} onClick={t27} variant="ghost">Cancel</Button>;
    $[55] = isBusy;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  const t29 = isSubmitted ? "bg-success text-success-foreground hover:bg-success disabled:opacity-100" : undefined;
  let t30;
  if ($[58] !== isPending || $[59] !== isSubmitted) {
    t30 = isSubmitted ? <><CheckIcon className="size-4" />Submitted</> : isPending ? "Submitting..." : "Submit";
    $[58] = isPending;
    $[59] = isSubmitted;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== handleSubmit || $[62] !== isBusy || $[63] !== t29 || $[64] !== t30) {
    t31 = <Button className={t29} disabled={isBusy} onClick={handleSubmit}>{t30}</Button>;
    $[61] = handleSubmit;
    $[62] = isBusy;
    $[63] = t29;
    $[64] = t30;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== t28 || $[67] !== t31) {
    t32 = <DialogFooter>{t28}{t31}</DialogFooter>;
    $[66] = t28;
    $[67] = t31;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== t12 || $[70] !== t26 || $[71] !== t32) {
    t33 = <DialogContent className="sm:max-w-md">{t12}{t26}{t32}</DialogContent>;
    $[69] = t12;
    $[70] = t26;
    $[71] = t32;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== handleOpenChange || $[74] !== open || $[75] !== t33) {
    t34 = <Dialog onOpenChange={handleOpenChange} open={open}>{t33}</Dialog>;
    $[73] = handleOpenChange;
    $[74] = open;
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  return t34;
}